import { Goal, ImpactLevel } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { GoalSortingMode } from 'shared/types/sorting-mode';

import { getCompletionPercentage } from './get-completion-percentage';
import { getGoalCompletion } from './get-goal-completion';
import { sortItems } from './sort-items';

export type Options = {
  allGoals: Goal[];
  sortMode?: GoalSortingMode;
  customOrder?: ID[] | null;
  invertStartDateSorting?: boolean;
  lifeAreaOrder?: Map<ID, number>;
};

export const sortGoals = (
  goals: Goal[],
  {
    allGoals,
    sortMode,
    customOrder,
    invertStartDateSorting,
    lifeAreaOrder,
  }: Options,
) => {
  if (sortMode === GoalSortingMode.Custom) {
    return sortItems(goals, customOrder ?? [], 'id');
  }

  if (sortMode === GoalSortingMode.StartDate) {
    return goals.toSorted((goalA, goalB) => {
      const { startDate: startDateA } = goalA;
      const { startDate: startDateB } = goalB;

      if (!startDateA && !startDateB) {
        return 0;
      }
      if (!startDateA) {
        return 1;
      }
      if (!startDateB) {
        return -1;
      }

      const dateA = invertStartDateSorting ? startDateB : startDateA;
      const dateB = invertStartDateSorting ? startDateA : startDateB;
      return dateA.getTime() - dateB.getTime();
    });
  }

  if (sortMode === GoalSortingMode.Deadline) {
    return goals.toSorted(
      ({ deadline: deadlineA }, { deadline: deadlineB }) => {
        if (!deadlineA && !deadlineB) {
          return 0;
        }
        if (!deadlineA) {
          return 1;
        }
        if (!deadlineB) {
          return -1;
        }

        return deadlineA.getTime() - deadlineB.getTime();
      },
    );
  }

  if (sortMode === GoalSortingMode.Name) {
    return goals.toSorted((goalA, goalB) =>
      goalA.name.localeCompare(goalB.name),
    );
  }

  if (sortMode === GoalSortingMode.Tasks) {
    return goals.toSorted((goalA, goalB) => {
      const tasksA = goalA.taskCount ?? 0;
      const tasksB = goalB.taskCount ?? 0;
      return tasksB - tasksA;
    });
  }

  if (sortMode === GoalSortingMode.Impact) {
    const ranking: Record<ImpactLevel, number> = {
      [ImpactLevel.High]: 0,
      [ImpactLevel.Medium]: 1,
      [ImpactLevel.Low]: 2,
    };
    return goals.toSorted((goalA, goalB) => {
      const rankA = goalA.impact != null ? ranking[goalA.impact] : 3;
      const rankB = goalB.impact != null ? ranking[goalB.impact] : 3;
      return rankA - rankB;
    });
  }

  if (sortMode === GoalSortingMode.Progress) {
    return goals.toSorted((goalA, goalB) => {
      const goalACompletion = getGoalCompletion(goalA, { goals: allGoals });
      const goalBCompletion = getGoalCompletion(goalB, { goals: allGoals });

      if (goalBCompletion.end === Infinity) {
        return -1;
      }

      if (goalACompletion.end === Infinity) {
        return 1;
      }

      const completionPercentageA = getCompletionPercentage(goalACompletion);
      const completionPercentageB = getCompletionPercentage(goalBCompletion);

      return completionPercentageB - completionPercentageA;
    });
  }

  if (sortMode === GoalSortingMode.LifeArea && lifeAreaOrder) {
    return goals.toSorted((goalA, goalB) => {
      const orderA = goalA.lifeAreaId
        ? lifeAreaOrder.get(goalA.lifeAreaId) ?? Infinity
        : Infinity;
      const orderB = goalB.lifeAreaId
        ? lifeAreaOrder.get(goalB.lifeAreaId) ?? Infinity
        : Infinity;
      return orderA - orderB;
    });
  }

  return goals;
};
